/**
 * HUD — the player-facing interface.
 *
 * Everything here is DOM inside `#ui-root`. That buys crisp text at any DPI for
 * free, and costs essentially nothing *provided* we never touch the document
 * during a frame unless something actually changed. So: every field caches its
 * last written value, all writes are guarded by an equality check, and nothing in
 * the update path ever reads layout (`offsetWidth`, `getBoundingClientRect`,
 * `getComputedStyle`) — a single one of those would force a synchronous reflow
 * and cost more than the entire render.
 *
 * Owns: ammo, health, score/XP, the low-health feedback wiring, the F3 debug
 * overlay, and the lifecycle of the sub-widgets.
 */

import { Crosshair } from './Crosshair.js';
import { DamageIndicator } from './DamageIndicator.js';
import { Killfeed } from './Killfeed.js';
import { Minimap } from './Minimap.js';
import { Menu } from './Menu.js';
import { applyI18n, onLang, t } from './i18n.js';

export const TUNING = {
  litAmmo: 0.9, // s the ammo block stays bright after a change
  litHealth: 1.7,
  litScore: 2.2,
  litCompass: 1.4,

  lowAmmoFrac: 0.3, // magazine fraction below which the counter goes amber
  shotPulse: 0.09, // s of the per-shot scale pulse
  shotPulseScale: 0.13,

  defaultReload: 2.0, // s, until a real reload has been timed
  reloadHold: 0.14, // s the completed bar stays visible

  xpKill: 100,
  xpHeadshot: 150,
  xpPopLife: 0.85,
  xpPopRise: 20, // px

  debugHz: 5,
};

export class HUD {
  constructor(ctx) {
    this.ctx = ctx;

    const host = document.getElementById('ui-root') ?? document.body;
    const root = document.createElement('div');
    root.id = 'hud';
    host.appendChild(root);
    this.root = root;

    // First of all, so it sits behind every readout in `#hud`. It still covers the
    // world completely, because the canvas is not in `#hud` at all — which is the
    // whole intent: a flashbang takes your sight, not your ammunition count.
    this._buildFlash(root);
    // Damage layers go in next so they sit behind every readout.
    this.damage = new DamageIndicator(ctx, root);
    this.minimap = new Minimap(ctx, root);
    this.killfeed = new Killfeed(ctx, root);
    this._buildAmmo(root);
    this._buildHealth(root);
    this._buildScore(root);
    this._buildTeamScore(root);
    this._buildEquipment(root);
    this.crosshair = new Crosshair(ctx, root);
    this._buildDebug(root);
    this.menu = new Menu(ctx, root);

    this.damage.onBearing = (deg) => {
      this.minimap.addHitMark(deg);
      this._litCompass = TUNING.litCompass;
    };
    this.menu.onVisibility = (open) => {
      root.classList.toggle('is-occluded', open);
    };

    // After every sub-widget has built its markup, and again on any language change.
    // The HUD lives for the whole session, so it cannot rely on being rebuilt.
    applyI18n(root);
    this._offLang = onLang(() => {
      applyI18n(root);
      // The XP pop is written by code rather than markup, so it is not covered by
      // the sweep above; it will pick the new language up on the next kill.
    });

    this._initState();
    this._bind();

    this._unregister = ctx.engine?.registerUpdate?.((dt) => this.update(dt), 'ui');
    this._exposeTestHooks();
  }

  /* -------------------------------- markup -------------------------------- */

  _buildAmmo(root) {
    const el = document.createElement('div');
    el.className = 'hud-ammo hud-fade';
    el.innerHTML = `
      <div class="hud-ammo-heat">
        <div class="hud-ammo-heat-label">HEAT</div>
        <div class="hud-ammo-heat-bar"><i></i><b></b></div>
      </div>
      <div class="hud-ammo-head">
        <div class="hud-ammo-name">—</div>
        <div class="hud-ammo-mode">SEMI</div>
      </div>
      <div class="hud-ammo-nums">
        <div class="hud-ammo-mag">0</div>
        <div class="hud-ammo-sep">/</div>
        <div class="hud-ammo-res">0</div>
      </div>
      <div class="hud-ammo-reload"><i></i></div>`;
    root.appendChild(el);
    this.ammoEl = el;
    this.ammoName = el.querySelector('.hud-ammo-name');
    this.ammoMode = el.querySelector('.hud-ammo-mode');
    this.ammoNums = el.querySelector('.hud-ammo-nums');
    this.ammoMag = el.querySelector('.hud-ammo-mag');
    this.ammoRes = el.querySelector('.hud-ammo-res');
    this.reloadEl = el.querySelector('.hud-ammo-reload');
    this.reloadFill = el.querySelector('.hud-ammo-reload i');
    this.heatEl = el.querySelector('.hud-ammo-heat');
    this.heatLabel = el.querySelector('.hud-ammo-heat-label');
    this.heatFill = el.querySelector('.hud-ammo-heat-bar i');
    this.heatMark = el.querySelector('.hud-ammo-heat-bar b');
  }

  _buildHealth(root) {
    const el = document.createElement('div');
    el.className = 'hud-health hud-fade';
    el.innerHTML = `
      <div class="hud-health-head">
        <div class="hud-label" data-i18n="hud.health"></div>
        <div class="hud-health-num">100</div>
      </div>
      <div class="hud-health-bar"><i></i></div>`;
    root.appendChild(el);
    this.healthEl = el;
    this.healthNum = el.querySelector('.hud-health-num');
    this.healthFill = el.querySelector('.hud-health-bar i');
  }

  _buildScore(root) {
    const el = document.createElement('div');
    el.className = 'hud-score hud-fade';
    el.innerHTML = `
      <div><b data-f="kills">0</b> <span data-i18n="hud.kills"></span></div>
      <div class="hud-score-xp"><b data-f="xp">0</b> <span data-i18n="hud.xp"></span></div>`;
    root.appendChild(el);
    this.scoreEl = el;
    this.killsEl = el.querySelector('[data-f="kills"]');
    this.xpEl = el.querySelector('[data-f="xp"]');

    this.xpPop = document.createElement('div');
    this.xpPop.className = 'hud-xppop hud-fade';
    root.appendChild(this.xpPop);
  }

  /**
   * The team scoreboard: two numbers and a clock, at the top of the screen.
   *
   * Absent unless a mode publishes `match:score`, which Skirmish never does — the
   * solo mode has no second team, and a 0–0 readout for a fight that has no sides
   * is worse than no readout. It appears the first time a score arrives and stays.
   *
   * Yours is always on the left, regardless of which team id you are on. A player
   * should never have to remember whether they are ALPHA today.
   */
  _buildTeamScore(root) {
    const el = document.createElement('div');
    el.className = 'hud-team hud-fade';
    el.hidden = true;
    el.innerHTML = `
      <div class="hud-team-side is-us"><b data-f="us">0</b></div>
      <div class="hud-team-mid"><span data-f="clock">--:--</span></div>
      <div class="hud-team-side is-them"><b data-f="them">0</b></div>`;
    root.appendChild(el);
    this.teamEl = el;
    this.teamUs = el.querySelector('[data-f="us"]');
    this.teamThem = el.querySelector('[data-f="them"]');
    this.teamClock = el.querySelector('[data-f="clock"]');
  }

  _onMatchScore(p) {
    if (!p?.score || !this.teamEl) return;
    const mine = this.ctx.match?.teamOf(this.ctx.player) ?? 0;
    const theirs = mine === 0 ? 1 : 0;
    if (this.teamEl.hidden) {
      this.teamEl.hidden = false;
      // Everything else at the top centre moves down by the scoreboard's height.
      // One class, one variable — see `--team-h` in hud.css.
      this.root.classList.add('has-team');
    }
    this.teamUs.textContent = p.score[mine] ?? 0;
    this.teamThem.textContent = p.score[theirs] ?? 0;

    if (p.timeLimit > 0) {
      const left = Math.max(0, Math.ceil(p.timeLimit - p.elapsed));
      const mm = Math.floor(left / 60);
      const ss = String(left % 60).padStart(2, '0');
      this.teamClock.textContent = `${mm}:${ss}`;
      // Under a minute is the only time the clock is information rather than decor.
      this.teamClock.classList.toggle('is-urgent', left <= 60);
    } else {
      this.teamClock.textContent = p.limit ? `/${p.limit}` : '';
    }
  }

  /**
   * The two equipment slots and how many are left.
   *
   * Count first, name second, and both small: what a player needs mid-fight is
   * "have I still got one", which is a single digit. The name is there for the
   * three seconds after a loadout change when they have forgotten what they picked.
   */
  _buildEquipment(root) {
    const el = document.createElement('div');
    el.className = 'hud-equip hud-fade';
    el.innerHTML = `
      <div class="hud-eq-row" data-eq="lethal">
        <b class="hud-eq-n">2</b><span class="hud-eq-name">—</span>
      </div>
      <div class="hud-eq-row" data-eq="tactical">
        <b class="hud-eq-n">2</b><span class="hud-eq-name">—</span>
      </div>
      <div class="hud-eq-cook"><i></i></div>`;
    root.appendChild(el);
    this.equipEl = el;
    this.eqRows = {
      lethal: el.querySelector('[data-eq="lethal"]'),
      tactical: el.querySelector('[data-eq="tactical"]'),
    };
    this.cookEl = el.querySelector('.hud-eq-cook');
    this.cookFill = el.querySelector('.hud-eq-cook i');
  }

  /**
   * The white-out from a flashbang.
   *
   * A separate layer rather than a filter on the whole HUD, because the point is
   * that it covers the *world* and leaves the readouts legible underneath — being
   * flashed should take your sight, not your ammunition count.
   */
  _buildFlash(root) {
    this.flashEl = document.createElement('div');
    this.flashEl.className = 'hud-flash';
    root.appendChild(this.flashEl);
    this._flashT = 0;
    this._flashFor = 0;
  }

  _buildDebug(root) {
    this.debugEl = document.createElement('div');
    this.debugEl.className = 'hud-debug';
    root.appendChild(this.debugEl);
    this._debugOn = false;
    this._debugAcc = 0;
    this._hudMs = 0;

    // The engine zeroes `renderer.info` at the end of every tick, and the 'ui'
    // phase runs *before* the render — so by the time we look, the counters are
    // always zero. Snapshot them on the way past instead. Restored in dispose().
    this._renderStats = { calls: 0, triangles: 0 };
    const info = this.ctx.renderer?.info;
    if (info && typeof info.reset === 'function') {
      this._infoReset = info.reset.bind(info);
      info.reset = () => {
        this._renderStats.calls = info.render.calls;
        this._renderStats.triangles = info.render.triangles;
        this._infoReset();
      };
      this._patchedInfo = info;
    }

    this._setDebug = (on) => {
      this._debugOn = !!on;
      this.debugEl.classList.toggle('is-on', this._debugOn);
      // Force the next tick to write, or the readout keeps whatever numbers it had
      // when it was last hidden until the 5 Hz timer next comes round.
      this._debugAcc = 99;
    };
    this._onKey = (e) => {
      if (e.code !== 'F3') return;
      e.preventDefault();
      this._setDebug(!this._debugOn);
    };
    window.addEventListener('keydown', this._onKey);
    // The pause menu's Stats row, for devices with no F3 — which is every phone.
    this.ctx.events?.on?.('ui:stats', (p) => this._setDebug(p?.on));
  }

  /* --------------------------------- state -------------------------------- */

  _initState() {
    this.mag = 0;
    this.reserve = 0;
    this.magMax = 30;
    this.health = 100;
    this.healthMax = 100;
    this.kills = 0;
    this.xp = 0;

    this._litAmmo = 0;
    this._litHealth = 0;
    this._litScore = 0;
    this._litCompass = 0;
    this._pulse = 0;
    this._xpPopAge = -1;

    this._reloadT = -1;
    this._reloadDur = TUNING.defaultReload;
    this._reloadMeasured = new Map();
    this._reloadDone = -1;

    // null means "no heat gauge on this weapon", which is every weapon without a
    // belt feed fitted. Deliberately distinct from 0 — a cold belt-fed gun shows
    // an empty gauge, an ordinary gun shows nothing at all.
    //
    // We go back to null on every weapon switch and wait to be told again, rather
    // than trusting the 0 the behaviour module force-publishes when it clears. Both
    // fire off the same `weapon:switch`, so which one lands last is a listener
    // registration order I would rather not depend on. The cost is that the gauge
    // appears on the first round rather than the moment the gun comes up.
    this._heat = null;
    this._heatLocked = false;
    this._heatResume = 0.5;

    this._eq = null;
    this._litEquip = 0;
    this._cookFull = 0;

    // cached last-written DOM values
    this._w = {
      mag: null,
      res: null,
      name: null,
      mode: null,
      health: null,
      healthFill: -1,
      ammoLit: null,
      healthLit: null,
      scoreLit: null,
      compassLit: null,
      ammoLow: null,
      eq_lethal_n: null,
      eq_lethal_name: null,
      eq_tactical_n: null,
      eq_tactical_name: null,
      eqCook: null,
      eqCookFill: -1,
      equipLit: null,
      flash: 0,
      heatOn: null,
      heatFill: -1,
      heatBand: null,
      heatMark: -1,
      heatLabel: null,
      healthLow: null,
      kills: null,
      xp: null,
      pulse: -1,
      reloadOn: null,
      reloadFill: -1,
    };
  }

  _bind() {
    const ev = this.ctx.events;

    ev.on('weapon:ammo', (p) => {
      if (!p) return;
      const mag = p.mag ?? 0;
      if (mag < this.mag) this._pulse = TUNING.shotPulse;
      this.mag = mag;
      this.reserve = p.reserve ?? this.reserve;
      if (p.max) this.magMax = p.max;
      // The weapon system ships the display name and fire mode on this event;
      // prefer them over anything we can dig out of `ctx.weapons` ourselves.
      if (p.name) this._weaponName = p.name;
      if (p.fireMode) this._fireMode = p.fireMode;
      this._litAmmo = TUNING.litAmmo;
    });

    ev.on('weapon:switch', (p) => {
      // `to` may be an id string or the weapon object itself; `def` carries the
      // display name when the weapon system sends one.
      const to = p?.to;
      const name =
        p?.def?.name ?? (typeof to === 'object' && to ? to.displayName ?? to.name ?? to.id : to);
      this._weaponName = name ?? this._weaponName;
      if (p?.def?.fireMode) this._fireMode = p.def.fireMode;
      this._litAmmo = TUNING.litAmmo;
      this._reloadT = -1;
      this._heat = null;
      this._heatLocked = false;
    });

    ev.on('weapon:heat', (p) => {
      if (!p) return;
      const heat = Math.max(0, Math.min(1, p.ratio ?? p.heat ?? 0));
      const locked = !!p.locked;
      if (typeof p.resumeAt === 'number') this._heatResume = p.resumeAt;
      // Locking out is the one heat event the player must not miss — it is the
      // gun going quiet without them asking. Pull the block to full brightness.
      if (locked && !this._heatLocked) this._litAmmo = TUNING.litAmmo;
      this._heat = heat;
      this._heatLocked = locked;
    });

    ev.on('weapon:reload', (p) => {
      const name = this._weaponKey();
      if (p?.stage === 'start') {
        this._reloadT = 0;
        // Weapons publish an `empty` flag and carry both timings in their def,
        // so the bar is timed to the animation actually playing.
        const r = this.ctx.weapons?.current?.def?.reload;
        this._reloadDur =
          p.duration ??
          (r ? (p.empty ? r.empty : r.tactical) : undefined) ??
          this._reloadMeasured.get(name) ??
          TUNING.defaultReload;
        this._litAmmo = TUNING.litAmmo;
      } else if (p?.stage === 'end') {
        if (this._reloadT >= 0.15) this._reloadMeasured.set(name, this._reloadT);
        this._reloadT = -1;
        this._reloadDone = TUNING.reloadHold;
        this._litAmmo = TUNING.litAmmo;
      }
    });

    ev.on('player:health', (p) => {
      if (!p) return;
      this.healthMax = p.max || this.healthMax;
      const h = Math.max(0, Math.min(this.healthMax, p.health ?? this.healthMax));
      if (h !== this.health) this._litHealth = TUNING.litHealth;
      this.health = h;
    });

    ev.on('player:damage', () => {
      this._litHealth = TUNING.litHealth;
    });

    ev.on('actor:death', (p) => {
      if (p?.mine) this._scoreKill(!!p.headshot);
    });

    ev.on('match:score', (p) => this._onMatchScore(p));

    ev.on('equipment:state', (p) => {
      if (!p) return;
      this._eq = p;
      this._litEquip = TUNING.litAmmo;
    });
    ev.on('equipment:empty', () => {
      this._litEquip = TUNING.litAmmo;
      this.equipEl.classList.add('is-empty');
      setTimeout(() => this.equipEl.classList.remove('is-empty'), 260);
    });
    ev.on('player:flash', (p) => {
      const s = Math.max(0, p?.seconds ?? 0);
      // Take the longer of the two rather than adding: see Enemy.applyStatus. Two
      // flashes should be one long flash, not a blackout with no end.
      if (s > this._flashT) {
        this._flashT = s;
        this._flashFor = s;
      }
    });

    ev.on('game:state', (p) => {
      if (p?.state === 'dead') this._litHealth = TUNING.litHealth;
    });
  }

  _weaponKey() {
    return this._weaponName ?? this.ctx.weapons?.current?.name ?? 'default';
  }

  _scoreKill(headshot) {
    this.kills++;
    const gain = headshot ? TUNING.xpHeadshot : TUNING.xpKill;
    this.xp += gain;
    this._litScore = TUNING.litScore;
    this.xpPop.textContent = headshot ? `+${gain}  ${t('hud.headshot')}` : `+${gain}`;
    this._xpPopAge = 0;
  }

  /* --------------------------------- frame -------------------------------- */

  update(dt) {
    const t0 = performance.now();

    this.crosshair.update(dt);
    this.damage.update(dt);
    this.killfeed.update(dt);
    this.minimap.update(dt);
    this.menu.update(dt);

    this._updateAmmo(dt);
    this._updateHealth(dt);
    this._updateScore(dt);
    this._updateEquipment(dt);
    this._updateFlash(dt);
    this._updateCompassLit(dt);
    this._updateDebug(dt);

    this._hudMs += (performance.now() - t0 - this._hudMs) * 0.08;
  }

  _updateAmmo(dt) {
    const w = this._w;

    const mag = String(this.mag);
    if (mag !== w.mag) {
      this.ammoMag.textContent = mag;
      w.mag = mag;
    }
    const res = String(this.reserve);
    if (res !== w.res) {
      this.ammoRes.textContent = res;
      w.res = res;
    }

    const name = String(
      this._weaponName ??
        this.ctx.weapons?.current?.displayName ??
        this.ctx.weapons?.current?.name ??
        '—'
    ).toUpperCase();
    if (name !== w.name) {
      this.ammoName.textContent = name;
      w.name = name;
    }
    const mode = String(
      this._fireMode ?? this.ctx.weapons?.current?.def?.fireMode ?? 'SEMI'
    ).toUpperCase();
    if (mode !== w.mode) {
      this.ammoMode.textContent = mode;
      w.mode = mode;
    }

    // low / empty colouring
    const frac = this.magMax > 0 ? this.mag / this.magMax : 1;
    const low = this.mag > 0 && frac < TUNING.lowAmmoFrac ? 'low' : this.mag === 0 ? 'empty' : '';
    if (low !== w.ammoLow) {
      this.ammoEl.classList.toggle('is-low', low === 'low');
      this.ammoEl.classList.toggle('is-empty', low === 'empty');
      w.ammoLow = low;
    }

    this._updateHeat();

    // brightness
    if (this._litAmmo > 0) this._litAmmo -= dt;
    // A lockout holds the block lit for as long as it lasts: the readout that
    // explains why the trigger stopped working has to still be legible when the
    // player looks down at it, which may be a second after the gun went quiet.
    const lit = this._litAmmo > 0 || this._heatLocked;
    if (lit !== w.ammoLit) {
      this.ammoEl.classList.toggle('is-lit', lit);
      w.ammoLit = lit;
    }

    // per-shot pulse
    if (this._pulse > 0) {
      this._pulse -= dt;
      const p = Math.max(0, this._pulse / TUNING.shotPulse);
      const s = 1 + TUNING.shotPulseScale * p * p;
      const r = Math.round(s * 1000) / 1000;
      if (r !== w.pulse) {
        this.ammoNums.style.transform = `scale(${r})`;
        w.pulse = r;
      }
    } else if (w.pulse !== 1 && w.pulse !== -1) {
      this.ammoNums.style.transform = 'scale(1)';
      w.pulse = 1;
    }

    // reload progress
    let show = false;
    let fill = 0;
    if (this._reloadT >= 0) {
      this._reloadT += dt;
      show = true;
      fill = Math.min(1, this._reloadT / Math.max(0.05, this._reloadDur));
    } else if (this._reloadDone > 0) {
      this._reloadDone -= dt;
      show = true;
      fill = 1;
    }
    if (show !== w.reloadOn) {
      this.reloadEl.classList.toggle('is-on', show);
      w.reloadOn = show;
    }
    if (show) {
      const r = Math.round(fill * 200) / 200;
      if (r !== w.reloadFill) {
        this.reloadFill.style.transform = `scaleX(${r})`;
        w.reloadFill = r;
      }
    }
  }

  /**
   * The heat gauge, driven entirely off `weapon:heat`.
   *
   * The tick on the bar is `resumeAt` — the level heat has to fall back through
   * before the gun will fire again. Without it a lockout is an opaque wait; with
   * it the player can see how long they have and, more usefully, learns where to
   * stop the burst next time.
   */
  _updateHeat() {
    const w = this._w;
    const on = this._heat !== null;
    if (on !== w.heatOn) {
      this.heatEl.classList.toggle('is-on', on);
      w.heatOn = on;
    }
    if (!on) return;

    const r = Math.round(this._heat * 200) / 200;
    if (r !== w.heatFill) {
      this.heatFill.style.transform = `scaleX(${r})`;
      w.heatFill = r;
    }

    const mark = Math.round(this._heatResume * 1000) / 10;
    if (mark !== w.heatMark) {
      this.heatMark.style.right = `${mark}%`;
      w.heatMark = mark;
    }

    // Amber before the bar is full is the only warning that exists — there is no
    // audio cue until the lockout itself, by which point it is advice about the
    // next burst rather than this one.
    const band = this._heatLocked ? 'lock' : this._heat >= 0.75 ? 'warn' : '';
    if (band !== w.heatBand) {
      this.heatEl.classList.toggle('is-warn', band === 'warn');
      this.heatEl.classList.toggle('is-lock', band === 'lock');
      w.heatBand = band;
    }

    const pct = Math.round(this._heat * 100);
    const label = this._heatLocked ? `VENTING ${pct}%` : `HEAT ${pct}%`;
    if (label !== w.heatLabel) {
      this.heatLabel.textContent = label;
      w.heatLabel = label;
    }
  }

  _updateHealth(dt) {
    const w = this._w;
    const num = String(Math.ceil(this.health));
    if (num !== w.health) {
      this.healthNum.textContent = num;
      w.health = num;
    }
    const frac = this.healthMax > 0 ? this.health / this.healthMax : 1;
    const r = Math.round(frac * 200) / 200;
    if (r !== w.healthFill) {
      this.healthFill.style.transform = `scaleX(${r})`;
      w.healthFill = r;
    }
    const band = frac <= 0.35 ? 'crit' : frac <= 0.7 ? 'hurt' : '';
    if (band !== w.healthLow) {
      this.healthEl.classList.toggle('is-hurt', band === 'hurt');
      this.healthEl.classList.toggle('is-crit', band === 'crit');
      w.healthLow = band;
    }
    if (this._litHealth > 0) this._litHealth -= dt;
    const lit = this._litHealth > 0 || band === 'crit';
    if (lit !== w.healthLit) {
      this.healthEl.classList.toggle('is-lit', lit);
      w.healthLit = lit;
    }
  }

  _updateScore(dt) {
    const w = this._w;
    const k = String(this.kills);
    if (k !== w.kills) {
      this.killsEl.textContent = k;
      w.kills = k;
    }
    const x = String(this.xp);
    if (x !== w.xp) {
      this.xpEl.textContent = x;
      w.xp = x;
    }
    if (this._litScore > 0) this._litScore -= dt;
    const lit = this._litScore > 0;
    if (lit !== w.scoreLit) {
      this.scoreEl.classList.toggle('is-lit', lit);
      w.scoreLit = lit;
    }

    if (this._xpPopAge >= 0) {
      this._xpPopAge += dt;
      const t = this._xpPopAge / TUNING.xpPopLife;
      if (t >= 1) {
        this._xpPopAge = -1;
        this.xpPop.style.opacity = '0';
      } else {
        const rise = -TUNING.xpPopRise * (1 - (1 - t) * (1 - t));
        const o = t < 0.25 ? t / 0.25 : 1 - (t - 0.25) / 0.75;
        this.xpPop.style.transform = `translate(-50%, ${rise.toFixed(1)}px)`;
        this.xpPop.style.opacity = o.toFixed(2);
      }
    }
  }

  _updateEquipment(dt) {
    const w = this._w;
    const eq = this._eq;
    if (!eq) return;

    for (const slot of ['lethal', 'tactical']) {
      const row = this.eqRows[slot];
      const n = String(eq[`${slot}Count`] ?? 0);
      if (n !== w[`eq_${slot}_n`]) {
        row.querySelector('.hud-eq-n').textContent = n;
        row.classList.toggle('is-out', n === '0');
        w[`eq_${slot}_n`] = n;
      }
      const name = this._equipName(eq[slot]);
      if (name !== w[`eq_${slot}_name`]) {
        row.querySelector('.hud-eq-name').textContent = name;
        w[`eq_${slot}_name`] = name;
      }
    }

    // The cook bar. Only ever visible while the button is held on a cookable
    // lethal, which is exactly the window where the player needs to know how much
    // fuse is left and has no other way to find out.
    const cooking = !!eq.cooking && eq.cookLeft > 0;
    if (cooking !== w.eqCook) {
      this.cookEl.classList.toggle('is-on', cooking);
      w.eqCook = cooking;
    }
    if (cooking) {
      const full = this._cookFull || (this._cookFull = eq.cookLeft);
      const k = Math.max(0, Math.min(1, eq.cookLeft / full));
      const r = Math.round(k * 100) / 100;
      if (r !== w.eqCookFill) {
        this.cookFill.style.transform = `scaleX(${r})`;
        this.cookFill.style.background = k < 0.35 ? 'var(--bad)' : 'var(--accent)';
        w.eqCookFill = r;
      }
    } else {
      this._cookFull = 0;
    }

    if (this._litEquip > 0) this._litEquip -= dt;
    const lit = this._litEquip > 0 || cooking;
    if (lit !== w.equipLit) {
      this.equipEl.classList.toggle('is-lit', lit);
      w.equipLit = lit;
    }
  }

  /**
   * Display name for an equipment id.
   *
   * Read off the live system rather than importing the table: the HUD has no
   * business knowing where equipment is defined, and `ctx.equipment.defs` exists
   * for exactly this — the same seam `WeaponSystem.defs` provides for weapons.
   */
  _equipName(id) {
    if (!id) return '—';
    const def = this.ctx.equipment?.defs?.[id];
    if (!def) return String(id).toUpperCase();
    return (t('hud.kills') === 'KILLS' ? def.nameEn || def.name : def.name) ?? id;
  }

  /**
   * Flash decay.
   *
   * Full white for the first fifth, then a fast falloff. That shape matters: a
   * linear fade reads as a slow screen wipe, while a hold-then-drop reads as eyes
   * recovering, which is what it is meant to be.
   */
  _updateFlash(dt) {
    if (this._flashT <= 0) {
      if (this._w.flash !== 0) {
        this.flashEl.style.opacity = '0';
        this._w.flash = 0;
      }
      return;
    }
    this._flashT -= dt;
    const k = Math.max(0, this._flashT / Math.max(0.01, this._flashFor));
    const a = k > 0.8 ? 1 : Math.pow(k / 0.8, 1.6);
    const r = Math.round(a * 100) / 100;
    if (r !== this._w.flash) {
      this.flashEl.style.opacity = String(r);
      this._w.flash = r;
    }
  }

  _updateCompassLit(dt) {
    const w = this._w;
    if (this._litCompass > 0) this._litCompass -= dt;
    const lit = this._litCompass > 0;
    if (lit !== w.compassLit) {
      this.minimap.compass.classList.toggle('is-lit', lit);
      w.compassLit = lit;
    }
  }

  _updateDebug(dt) {
    if (!this._debugOn) return;
    this._debugAcc += dt;
    if (this._debugAcc < 1 / TUNING.debugHz) return;
    this._debugAcc = 0;
    const e = this.ctx.engine;
    const info = this.ctx.renderer?.info;
    const p = this.ctx.player?.position;
    this.debugEl.textContent =
      `${(e?.stats?.fps ?? 0).toFixed(0)} FPS   ${(e?.stats?.ms ?? 0).toFixed(2)} ms\n` +
      `draw ${this._renderStats.calls}   tri ${this._renderStats.triangles | 0}\n` +
      `prog ${info?.programs?.length ?? 0}   geo ${info?.memory?.geometries ?? 0}   tex ${info?.memory?.textures ?? 0}\n` +
      `hud  ${this._hudMs.toFixed(3)} ms\n` +
      (p ? `pos  ${p.x.toFixed(1)} ${p.y.toFixed(1)} ${p.z.toFixed(1)}` : '');
  }

  /* ------------------------------ dev harness ----------------------------- */

  /**
   * Lets the HUD be driven without the weapon / AI / player systems, which are
   * built concurrently. Purely additive — it never changes runtime behaviour.
   */
  _exposeTestHooks() {
    const ev = this.ctx.events;
    window.__hudtest = {
      damage: (dirOrDeg = 0, amount = 24) => {
        let dir;
        if (typeof dirOrDeg === 'number') {
          // Interpret as a view-relative bearing in degrees.
          const yaw = this.ctx.player?.yaw ?? 0;
          const a = (dirOrDeg * Math.PI) / 180;
          const fx = -Math.sin(yaw);
          const fz = -Math.cos(yaw);
          const rx = Math.cos(yaw);
          const rz = -Math.sin(yaw);
          const ax = fx * Math.cos(a) + rx * Math.sin(a);
          const az = fz * Math.cos(a) + rz * Math.sin(a);
          dir = { x: -ax, y: 0, z: -az };
        } else dir = dirOrDeg;
        this.health = Math.max(0, this.health - amount);
        ev.emit('player:damage', { amount, dir, health: this.health });
        ev.emit('player:health', { health: this.health, max: this.healthMax });
      },
      hit: (headshot = false) =>
        ev.emit('hit:actor', { actor: null, damage: 25, headshot, dir: null }),
      kill: (headshot = false) => {
        // `mine` because this handle exists to drive the player's own readouts;
        // without it the kill counter it is meant to exercise ignores the event.
        ev.emit('actor:death', {
          actor: { id: 'TANGO-0' + ((this.kills % 9) + 1) },
          headshot,
          mine: true,
        });
        ev.emit('ui:killfeed', {
          text: 'TANGO-0' + ((this.kills % 9) + 1),
          headshot,
        });
      },
      ammo: (mag = 24, reserve = 120, max = 30) => ev.emit('weapon:ammo', { mag, reserve, max }),
      health: (h = 100) => {
        this.health = h;
        ev.emit('player:health', { health: h, max: this.healthMax });
      },
      reload: (dur = 1.8) => {
        ev.emit('weapon:reload', { weapon: 'test', stage: 'start' });
        setTimeout(() => ev.emit('weapon:reload', { weapon: 'test', stage: 'end' }), dur * 1000);
      },
      weapon: (name = 'M4A1') => ev.emit('weapon:switch', { from: null, to: name }),
      heat: (ratio = 0.5, locked = false, resumeAt = 0.35) =>
        ev.emit('weapon:heat', {
          heat: ratio,
          ratio,
          max: 1,
          locked,
          venting: locked,
          resumeAt,
        }),
      state: (s) => ev.emit('player:state', s ?? {}),
      hud: this,
    };
  }

  dispose() {
    this._unregister?.();
    this._offLang?.();
    if (this._patchedInfo) this._patchedInfo.reset = this._infoReset;
    window.removeEventListener('keydown', this._onKey);
    this.crosshair.dispose();
    this.root.remove();
    if (window.__hudtest?.hud === this) delete window.__hudtest;
  }
}
